import { useEffect, useState, useSyncExternalStore } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'

import CustomBottomSheet from 'components/bottomSheet'
import { resolvePlatform } from 'modules/miniapp/detect'
import {
    clearPendingHandoff,
    fundsReturnPath,
    getPendingHandoff,
    isFramed,
    messengerName,
    readResultMessage,
    reopenGateway,
    subscribeHandoff,
} from '.'

interface RouterLike {
    navigate: (to: string, opts?: { replace?: boolean }) => unknown
}

/**
 * The framed mini app's half of a gateway payment in a new tab.
 *
 * Mounted once for the whole app and silent until `goToGateway` has sent a
 * payer out of a frame. Then it says where the bank went — or, when the browser
 * refused to open the tab without a fresh tap, offers the button that opens it
 * — and waits for the result tab to report back. See `modules/gatewayHandoff`.
 */
export default function GatewayHandoffSheet({
    router,
}: {
    router: RouterLike
}) {
    const pending = useSyncExternalStore(subscribeHandoff, getPendingHandoff)
    const queryClient = useQueryClient()
    const [blocked, setBlocked] = useState(false)

    useEffect(() => {
        if (!pending) setBlocked(false)
    }, [pending])

    // The result tab posts the verdict here before it closes itself. Listened
    // for whether or not the sheet is still up: a payer who dismissed it and
    // paid anyway is still owed the result.
    useEffect(() => {
        if (!isFramed()) return
        const onMessage = (event: MessageEvent) => {
            const outcome = readResultMessage(event)
            if (!outcome) return
            posthog.capture('gateway_external_result_received', {
                result: outcome.result,
                gateway: outcome.gateway,
                amount: outcome.amount,
                miniapp_platform: resolvePlatform(),
            })
            clearPendingHandoff()
            // Balances, dues and the fund list all changed at the bank.
            void queryClient.invalidateQueries()
            // The same address the backend sends an unframed payer to, so
            // `useGatewayReturn` counts it the same way and with the flow
            // this frame remembered before it sent the payer out.
            router.navigate(fundsReturnPath(outcome))
            if (outcome.result === 'success') {
                toast.success('پرداخت با موفقیت انجام شد')
            } else if (outcome.result === 'fail') {
                toast.error('پرداخت انجام نشد')
            }
        }
        window.addEventListener('message', onMessage)
        return () => window.removeEventListener('message', onMessage)
    }, [queryClient, router])

    // Coming back to the messenger tab by hand — the result tab could not
    // close itself, or the bank cut off its way back to us. Whatever happened
    // at the bank, what is on screen is stale.
    useEffect(() => {
        if (!pending?.opened) return
        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                void queryClient.invalidateQueries()
            }
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => document.removeEventListener('visibilitychange', onVisible)
    }, [pending?.opened, queryClient])

    if (!pending) return null

    const messenger = messengerName(resolvePlatform()) ?? 'پیام‌رسان'

    const open = () => {
        // Synchronously inside the tap, which is the whole point of this
        // button: a popup blocker lets a tab through only from a fresh gesture.
        if (!reopenGateway()) setBlocked(true)
    }

    const done = () => {
        posthog.capture('gateway_external_done_tapped', {
            opened: pending.opened,
        })
        clearPendingHandoff()
        void queryClient.invalidateQueries()
        router.navigate('/funds')
    }

    const dismiss = () => {
        posthog.capture('gateway_external_dismissed', {
            opened: pending.opened,
        })
        clearPendingHandoff()
    }

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(pending.url)
            toast.success('لینک پرداخت کپی شد')
            posthog.capture('gateway_external_link_copied')
        } catch {
            toast.error('کپی نشد؛ لینک را از کادر بالا بردارید')
        }
    }

    return (
        <CustomBottomSheet isOpen={!!pending} onDismiss={dismiss}>
            <div className="flex flex-col gap-4 pb-2" dir="rtl">
                {pending.opened ? (
                    <>
                        <div>
                            <p className="text-base font-bold">
                                درگاه پرداخت در صفحهٔ جدید باز شد
                            </p>
                            <p className="text-sm text-text-gray mt-2 leading-7">
                                {messenger} اجازه نمی‌دهد صفحهٔ بانک داخل خودش
                                باز شود؛ برای همین درگاه را در صفحهٔ جدید مرورگر
                                باز کردیم. پرداخت را همان‌جا تمام کنید. بعد از
                                پرداخت، دکمهٔ «بازگشت به {messenger}» شما را به
                                اینجا برمی‌گرداند.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={done}
                            className="bg-green-1 text-white text-sm rounded-md py-3"
                        >
                            پرداخت را تمام کردم
                        </button>
                        <button
                            type="button"
                            onClick={open}
                            className="text-green-1 text-sm rounded-md py-2 border border-green-1"
                        >
                            صفحهٔ پرداخت باز نشد؟ دوباره باز کنید
                        </button>
                    </>
                ) : (
                    <>
                        <div>
                            <p className="text-base font-bold">
                                پرداخت در صفحهٔ جدید مرورگر
                            </p>
                            <p className="text-sm text-text-gray mt-2 leading-7">
                                {messenger} اجازه نمی‌دهد صفحهٔ بانک داخل خودش
                                باز شود. درگاه پرداخت را در صفحهٔ جدید مرورگر
                                باز کنید؛ بعد از پرداخت به اینجا برمی‌گردید.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={open}
                            className="bg-green-1 text-white text-sm rounded-md py-3"
                        >
                            باز کردن درگاه پرداخت
                        </button>
                    </>
                )}

                {blocked && (
                    <div className="flex flex-col gap-2">
                        <p className="text-xs text-red-1 leading-6">
                            مرورگر جلوی باز شدن صفحهٔ جدید را گرفت. لینک پرداخت
                            را کپی کنید و در مرورگر باز کنید.
                        </p>
                        <input
                            readOnly
                            dir="ltr"
                            value={pending.url}
                            onFocus={(e) => e.currentTarget.select()}
                            className="text-xs border border-gray-1 rounded-md p-2 w-full"
                        />
                        <button
                            type="button"
                            onClick={copy}
                            className="text-sm rounded-md py-2 border border-gray-1"
                        >
                            کپی لینک پرداخت
                        </button>
                    </div>
                )}
            </div>
        </CustomBottomSheet>
    )
}
